//
// OVERVIEW OF THE GOAL VALUES RECALCULATE LOGIC, GENERATED BY AI
// https://gist.github.com/nulle/272a19a3ccea03d0d366bc827d38d9b2
//
import moment from 'moment';

import type { APIData } from 'webapp-types';
import { GoalCalculationStrategy, TimeUnit } from 'webapp/enums/enums';
import { sortBy } from 'webapp/utils/common';

import DateRange from 'webapp/models-custom/date-range';
import type { HistoryValues } from 'webapp/models-custom/goal';
import GoalValueRow from 'webapp/models-custom/goal/value-row';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

import type { GoalRangeTimeUnit } from 'webapp/services/model-manager/goals';

export const enum MetricTypeForGoalCalculations {
	AGGREGATABLE = 'aggregatable',
	NO_RESET = 'no_reset',
	CURRENT = 'current',
}

export const MetricTypeForCalculationStrategy: Record<
	GoalCalculationStrategy,
	MetricTypeForGoalCalculations
> = {
	[GoalCalculationStrategy.SUM]: MetricTypeForGoalCalculations.AGGREGATABLE,
	[GoalCalculationStrategy.AVERAGE]: MetricTypeForGoalCalculations.NO_RESET,
	[GoalCalculationStrategy.LAST_POINT]: MetricTypeForGoalCalculations.CURRENT,
};

export const CalculationStrategyForMetricType = Object.fromEntries(
	Object.entries(MetricTypeForCalculationStrategy).map(([strategy, metricType]) => [
		metricType,
		strategy,
	]),
) as Record<MetricTypeForGoalCalculations, GoalCalculationStrategy>;

export type TimeUnitForPages = Exclude<GoalRangeTimeUnit, TimeUnit.TOTAL>;
export const TargetEndDateFormat = DateRange.customDateFormat;

export type ValueRowsByTimeUnit = Record<TimeUnitForPages, GoalValueRow[]>;

export const PeriodIdFormats = {
	[TimeUnit.HOUR]: 'YYYY-MM-DD HH:mm',
	[TimeUnit.DAY]: 'YYYY-MM-DD',
	[TimeUnit.WEEK]: 'YYYY-[W]WW',
	[TimeUnit.MONTH]: 'YYYY-[M]MM',
	[TimeUnit.QUARTER]: 'YYYY-[Q0]Q',
	[TimeUnit.YEAR]: 'YYYY',
};

export const DEFAULT_REFERENCE_POINT_VALUE = 0;
export const DEFAULT_REFERENCE_POINT = new DChartPrimaryPoint({
	value: DEFAULT_REFERENCE_POINT_VALUE,
	valueStr: DEFAULT_REFERENCE_POINT_VALUE.toString(),
});

function recalculateAllValues(
	valueRowsByTimeUnit: ValueRowsByTimeUnit,
	primaryTimeUnit: GoalRangeTimeUnit,
	isTargetPercentual: boolean,
	targetValue: number,
	startDateTs: number | null,
	endDateTs: number | null,
	useEndDateForCalculations: boolean,
	metricType: MetricTypeForGoalCalculations,
	referencePointValue?: number | null,
	currentMomentRef = moment(), // used for tests only
) {
	Object.values(valueRowsByTimeUnit).forEach((valueRows) => {
		clearAllValues(valueRows);
	});

	const isPrimaryGranulationAllTime = primaryTimeUnit === TimeUnit.TOTAL;
	// we treat all time as daily periods
	const timeUnit = getGoalRangeTimeUnitForPrimaryGranulation(primaryTimeUnit);
	const primaryRows = valueRowsByTimeUnit[timeUnit];
	const calculationsReferencePointValue = referencePointValue || DEFAULT_REFERENCE_POINT_VALUE;
	const isForWholeTimeframe = !!startDateTs;

	logToConsole('recalculateAllValues', {
		referencePointValue,
		metricType,
		timeUnit,
		isTargetPercentual,
		startDateTs,
		endDateTs,
		useEndDateForCalculations,
		targetValue,
		isForWholeTimeframe,
	});

	// primary first
	recalculatePrimaryValues(
		primaryRows,
		isTargetPercentual,
		targetValue,
		startDateTs,
		endDateTs,
		useEndDateForCalculations,
		isPrimaryGranulationAllTime,
		metricType,
		calculationsReferencePointValue,
		currentMomentRef,
	);

	// all others from primary
	recalculateAllValuesFromPrimary(
		valueRowsByTimeUnit,
		timeUnit,
		metricType,
		startDateTs,
		endDateTs,
	);
}

// recalculate primary values for current period rows (main)
function recalculatePrimaryValues(
	primaryRows: GoalValueRow[],
	isTargetPercentual: boolean,
	targetValue: number,
	startDateTs: number | null,
	endDateTs: number | null,
	useEndDateForCalculations: boolean,
	isPrimaryGranulationAllTime = false,
	metricType: MetricTypeForGoalCalculations,
	referencePointValue?: number | null,
	currentMomentRef = moment(), // used for tests only
) {
	const calculationsReferencePointValue = referencePointValue || DEFAULT_REFERENCE_POINT_VALUE,
		isForWholeTimeframe = !!startDateTs;

	const primaryRowsForCalculation = getPrimaryRowsForCalculation(
		primaryRows,
		startDateTs,
		endDateTs,
	);
	const pastPrimaryRowsInTimeframe = isForWholeTimeframe
		? []
		: primaryRows.filter(
				(row) =>
					row.isPast &&
					(typeof startDateTs !== 'number' || row.periodStartTs >= startDateTs),
			);

	if (isTargetPercentual) {
		fillPercentualIncrease(
			primaryRowsForCalculation,
			calculationsReferencePointValue,
			targetValue,
			useEndDateForCalculations ? endDateTs : null,
			currentMomentRef,
		);
	} else if (endDateTs && useEndDateForCalculations) {
		// if end date is set

		const isMetricAggregatable = metricType === MetricTypeForGoalCalculations.AGGREGATABLE;
		if (isPrimaryGranulationAllTime && isMetricAggregatable) {
			// is all time date and fixed value goal
			fillDividedValueToDate(primaryRowsForCalculation, targetValue, endDateTs);
		} else {
			setLinearDiffToGetTo(
				primaryRowsForCalculation,
				calculationsReferencePointValue,
				targetValue,
				endDateTs,
			);
		}
	} else {
		// if fixed value goal and no end date
		fillSameValueForYear(primaryRowsForCalculation, targetValue);
	}

	// Fill completePrimaryRowsInTimeframe with the first value from primaryRowsForCalculation, if available
	if (pastPrimaryRowsInTimeframe.length && primaryRowsForCalculation.length) {
		const firstValue = primaryRowsForCalculation[0]!.value;
		fillValue(pastPrimaryRowsInTimeframe, firstValue);
	}
}

function recalculateDailyValues(
	dailyRows: GoalValueRow[], // rows to recalculate
	sourceRows: GoalValueRow[], // rows to get values from
	metricType: MetricTypeForGoalCalculations,
	redistribute = false,
	// currentMomentRef = moment(), // used for tests only
) {
	logToConsole('recalculateDailyValues', {
		metricType,
		dailyRows,
		sourceRows,
	});
	// on redistribute, we always recalculate all daily rows
	const rows = redistribute ? sourceRows : sourceRows.filter((r) => r.hasValue);

	const isMetricAggregatable = metricType === MetricTypeForGoalCalculations.AGGREGATABLE;

	rows.forEach((row) => {
		const affectedDailyRows = filterDailyRowsInsideBiggerPeriod(dailyRows, row);

		let dailyValue: number | null = null;
		if (isMetricAggregatable) {
			if (row.hasValue && affectedDailyRows.length > 0) {
				dailyValue = row.value! / affectedDailyRows.length;
			}
		} else {
			// current and noreset same logic
			dailyValue = row.value;
		}
		fillValue(affectedDailyRows, dailyValue);
	});
}

function recalculateAllValuesFromPrimary(
	valueRowsByTimeUnitToUpdate: ValueRowsByTimeUnit,
	primaryTimeUnit: TimeUnitForPages,
	metricType: MetricTypeForGoalCalculations,
	startDateTs: number | null = null,
	endDateTs: number | null = null,
	redistribute = false,
) {
	// we treat all time as daily periods
	const primaryRows = valueRowsByTimeUnitToUpdate[primaryTimeUnit]; // rows for primaryGranulation
	const primaryRowsForCalculation = getPrimaryRowsForCalculation(
		primaryRows,
		startDateTs,
		endDateTs,
	);

	logToConsole('recalculateAllValuesFromPrimary', {
		metricType,
		primaryTimeUnit,
		primaryRowsForCalculation,
	});

	// group other time units
	const allRowsByTimeUnit = valueRowsByTimeUnitToUpdate, // all rows
		smallerTimeUnitRowsTmp: Partial<ValueRowsByTimeUnit> = {}, // rows time unit smaller than primaryGranulation
		biggerTimeUnitRowsTmp: Partial<ValueRowsByTimeUnit> = {}; // rows time unit bigger than primaryGranulation
	Object.keys(allRowsByTimeUnit).forEach((timeUnitString: string) => {
		const timeUnit: TimeUnitForPages = +timeUnitString;
		if (timeUnit < primaryTimeUnit) {
			smallerTimeUnitRowsTmp[timeUnit] = allRowsByTimeUnit[timeUnit];
		} else if (timeUnit > primaryTimeUnit) {
			biggerTimeUnitRowsTmp[timeUnit] = allRowsByTimeUnit[timeUnit];
		}
	});

	const smallerTimeUnitRows = smallerTimeUnitRowsTmp as ValueRowsByTimeUnit,
		biggerTimeUnitRows = biggerTimeUnitRowsTmp as ValueRowsByTimeUnit;

	// update daily first, we use them for higher granulations
	let dailyRows = smallerTimeUnitRows[TimeUnit.DAY];
	if (dailyRows && primaryTimeUnit !== TimeUnit.DAY) {
		recalculateDailyValues(dailyRows, primaryRowsForCalculation, metricType, redistribute);
	} else {
		dailyRows = primaryRowsForCalculation;
	}

	// update smaller time units from daily
	Object.keys(smallerTimeUnitRows).forEach((timeUnitString: string) => {
		const timeUnit: TimeUnitForPages = +timeUnitString;
		if (timeUnit === TimeUnit.DAY) {
			// already handled daily
			return;
		}
		const rows = smallerTimeUnitRows[timeUnit];
		rows.forEach((r) => {
			setValueFromSmallerGranulation(r, dailyRows, metricType);
		});
	});

	// update bigger time units from primary
	Object.keys(biggerTimeUnitRows).forEach((timeUnitString: string) => {
		const timeUnit: TimeUnitForPages = +timeUnitString;

		const rows = biggerTimeUnitRows[timeUnit];
		rows.forEach((r) => {
			setValueFromSmallerGranulation(r, primaryRowsForCalculation, metricType);
		});
	});
}

function getPrimaryRowsForCalculation(
	primaryRows: GoalValueRow[],
	startDateTs: number | null,
	endDateTs: number | null,
) {
	const isForWholeTimeframe = !!startDateTs;
	if (isForWholeTimeframe) {
		// When calculating for whole timeframe, use rows within startDateTs and endDateTs
		return primaryRows.filter((row) => {
			const afterStart = typeof startDateTs !== 'number' || row.periodStartTs >= startDateTs;
			const beforeEnd = typeof endDateTs !== 'number' || row.periodStartTs < endDateTs;
			return afterStart && beforeEnd;
		});
	}
	return primaryRows.filter((row) => !row.isPast);
}

function getShouldRecalculateAllValues(
	isValidTargetValueEntered: boolean,
	isHistoryValuesOverridden: boolean,
	isHistoryValuesNotValid = true,
) {
	return isValidTargetValueEntered && isHistoryValuesNotValid && !isHistoryValuesOverridden;
}

// redistribute other time units except primaryTimeUnit
function redistributeFromTimeUnit(
	valueRowsByTimeUnit: ValueRowsByTimeUnit,
	primaryTimeUnitTmp: GoalRangeTimeUnit,
	sourceTimeUnit: TimeUnitForPages, // the one we redistribute from
	metricType: MetricTypeForGoalCalculations,
	startDateTs: number | null,
	endDateTs: number | null,
) {
	let valueRowsByTimeUnitToUpdate: Partial<ValueRowsByTimeUnit>; // possibly without one timeUnit
	const sourceTimeUnitValueRows = valueRowsByTimeUnit[sourceTimeUnit];

	// recalculate "by End Date" as daily primary time unit
	const primaryTimeUnit = getGoalRangeTimeUnitForPrimaryGranulation(primaryTimeUnitTmp);

	if (sourceTimeUnit === primaryTimeUnit) {
		valueRowsByTimeUnitToUpdate = valueRowsByTimeUnit;
	} else {
		const dailyRows = valueRowsByTimeUnit[TimeUnit.DAY];
		if (sourceTimeUnit !== TimeUnit.DAY) {
			// fill daily from sourceTimeUnit
			recalculateDailyValues(dailyRows, sourceTimeUnitValueRows, metricType, true);
		}
		// fill primary from daily
		const primaryTimeUnitValueRows = valueRowsByTimeUnit[primaryTimeUnit];
		recalculatePrimaryFromDailyValues(primaryTimeUnitValueRows, dailyRows, metricType);

		// fill all smaller than primary (except selectedTab and daily) from daily
		// filter out the rest to be updated from primary (we don't want daily to be updated from primary)
		valueRowsByTimeUnitToUpdate = {};
		Object.keys(valueRowsByTimeUnit).forEach((timeUnitString: string) => {
			const timeUnit: TimeUnitForPages = +timeUnitString;
			const rows = valueRowsByTimeUnit[timeUnit];
			if (timeUnit !== sourceTimeUnit) {
				if (timeUnit < primaryTimeUnit && timeUnit !== TimeUnit.DAY) {
					rows.forEach((r) => {
						setValueFromSmallerGranulation(r, dailyRows, metricType);
					});
				} else {
					valueRowsByTimeUnitToUpdate[timeUnit] = rows;
				}
			}
		});
	}
	// fill all (except the selectedTab) from primary
	recalculateAllValuesFromPrimary(
		valueRowsByTimeUnitToUpdate as ValueRowsByTimeUnit,
		primaryTimeUnit,
		metricType,
		startDateTs,
		endDateTs,
		true,
	);

	Object.values(valueRowsByTimeUnit).forEach((valueRows) => {
		markAllValuesAsClean(valueRows);
	});
}

function recalculatePrimaryFromDailyValues(
	primaryRows: GoalValueRow[],
	dailyRows: GoalValueRow[], // rows to recalculate
	metricType: MetricTypeForGoalCalculations,
) {
	// used in redistribute - when we update pages from non-primary
	logToConsole('recalculatePrimaryFromDailyValues', {
		primaryRows,
		dailyRows,
		metricType,
	});

	primaryRows.forEach((row) => {
		setValueFromSmallerGranulation(row, dailyRows, metricType);
	});
}

function markAllValuesAsClean(rows: GoalValueRow[]) {
	rows.forEach((r) => r.markClean());
}

function clearAllValues(rows: GoalValueRow[]) {
	rows.forEach((r) => (r.value = null));
}

function setLinearDiffToGetTo(
	rows: GoalValueRow[],
	startValue: number,
	endValue: number,
	endDateTs: number | null,
) {
	// startValue is the value before the first row
	// endValue is the value that last row has to be on
	let lastRowToSet = endDateTs ? findRowWithTimestamp(rows, endDateTs) : null;
	if (!lastRowToSet) {
		lastRowToSet = rows.slice(-1)[0];
	}
	if (lastRowToSet) {
		const totalDiff = endValue - startValue,
			rowsToUpdate = rows.slice(0, rows.indexOf(lastRowToSet) + 1),
			diffChangePerPeriod = totalDiff / rowsToUpdate.length;

		logToConsole('setLinearDiffToGetTo', {
			timeUnit: lastRowToSet.timeUnit,
			totalDiff,
			startValue,
			endValue,
			diffChangePerPeriod,
			rowsToUpdate,
		});

		rowsToUpdate.forEach((r, i) => {
			const diffAccumulated = diffChangePerPeriod * (i + 1),
				value = startValue + diffAccumulated;
			r.updateValue(value);
		});
	}
}

function fillPercentualIncrease(
	rows: GoalValueRow[],
	startValue: number,
	percent: number,
	endDateTs: number | null,
	currentMomentRef = moment(), // used for tests only
) {
	// startValue is the value before the first row
	// percent is by how many percent each row must increase
	let rowsToUpdate: GoalValueRow[] = [];
	if (endDateTs) {
		const lastRowToSet = findRowWithTimestamp(rows, endDateTs);
		if (lastRowToSet) {
			rowsToUpdate = rows.slice(0, rows.indexOf(lastRowToSet) + 1);
		} else {
			rowsToUpdate = rows;
		}
	} else {
		rowsToUpdate = filterMaxRowsToFill(rows, currentMomentRef);
	}
	let previousValue = startValue;

	logToConsole('fillPercentualIncrease', {
		timeUnit: rowsToUpdate[0]?.timeUnit,
		previousValue,
		percent,
		rowsToUpdate,
	});

	// update previous row with reference value
	rowsToUpdate[0]!.previousRow?.updateValue(previousValue);

	rowsToUpdate.forEach((r) => {
		const value = previousValue + (percent / 100) * previousValue;
		r.updateValue(value);
		previousValue = value;
	});
}

function fillValue(rows: GoalValueRow[], value: number | null) {
	rows.forEach((row) => {
		row.updateValue(value);
	});
}

function fillDividedValueToDate(rows: GoalValueRow[], value: number | null, endDateTs: number) {
	const affectedRows = rows.filter((row) => row.periodStartTs < endDateTs);

	const dividedValue = value !== null ? value / affectedRows.length : null;

	fillValue(affectedRows, dividedValue);
}

function fillSameValueForYear(rows: GoalValueRow[], value: number | null) {
	fillValue(rows, value);
	logToConsole('fillSameValueForYear', {
		timeUnit: rows[0]?.timeUnit,
		value,
	});
}

function filterMaxRowsToFill(rows: GoalValueRow[], currentMomentRef = moment()) {
	const endTs = currentMomentRef.clone().add(1, 'year').unix();
	return rows.filter((row) => {
		return row.periodStartTs < endTs;
	});
}

function setValueFromSmallerGranulation(
	row: GoalValueRow,
	smallerPeriodRows: GoalValueRow[],
	metricType: MetricTypeForGoalCalculations,
) {
	const isMetricCurrent = metricType === MetricTypeForGoalCalculations.CURRENT,
		isMetricNoReset = metricType === MetricTypeForGoalCalculations.NO_RESET,
		isMetricAggregatable = metricType === MetricTypeForGoalCalculations.AGGREGATABLE;

	const smallerPeriodRowsWithPartialsInRow = filterRowsWithPartialsInsideBiggerPeriod(
		smallerPeriodRows,
		row,
	);

	let value: number | null = null;

	if (isMetricAggregatable) {
		// fill also with fallback values, bcos sum
		let previousValue: number | null = null;
		const hasAnyValue = smallerPeriodRowsWithPartialsInRow.find((p) => p.row.hasValue);
		const valuesWithFallbackAndPartials = hasAnyValue
			? smallerPeriodRowsWithPartialsInRow.map((p, idx) => {
					// take first if at start, and previous if at end
					let value: number | null = null;
					if (p.row.hasValue) {
						value = p.row.value;
					} else {
						const nextWithValue = smallerPeriodRowsWithPartialsInRow
							.slice(idx + 1)
							.find((p) => p.row.hasValue);
						if (nextWithValue) {
							value = nextWithValue.row.value;
						} else {
							value = previousValue;
						}
					}
					previousValue = value;
					return { value: value!, percent: p.percent };
				})
			: [];

		const values = valuesWithFallbackAndPartials.map((item) => {
			return item.value * item.percent;
		});
		value = values.length ? values.map((v) => v || 0).reduce((s, v) => s + v, 0) : null;
	} else {
		// just the ones with values, since we avg / latest them
		const rowsPartialsWithValues = smallerPeriodRowsWithPartialsInRow.filter(
			(p) => p.row.hasValue,
		);
		if (isMetricCurrent) {
			// last
			const lastObjectRowValue = rowsPartialsWithValues.slice(-1)[0]?.row.value;
			value = typeof lastObjectRowValue === 'number' ? lastObjectRowValue : null;
		} else if (isMetricNoReset) {
			// avg
			const count = rowsPartialsWithValues.reduce((s, p) => s + p.percent, 0);
			value = count
				? rowsPartialsWithValues.map((p) => p.row.value!).reduce((s, v) => s + v, 0) / count
				: null;
		}
	}

	row.updateValue(value);
}

function filterRowsWithPartialsInsideBiggerPeriod(
	rows: GoalValueRow[],
	biggerPeriodRow?: GoalValueRow,
) {
	// percent shows how big part (0-1) of the row is inside the bigger period
	const rowsWithPercentage: { percent: number; row: GoalValueRow }[] = [];
	if (biggerPeriodRow) {
		const biggerPeriodSeconds = biggerPeriodRow.periodEndTs - biggerPeriodRow.periodStartTs;
		rows.forEach((r) => {
			if (
				r.periodStartTs < biggerPeriodRow.periodEndTs &&
				r.periodEndTs > biggerPeriodRow.periodStartTs
			) {
				const rowPeriodSeconds = r.periodEndTs - r.periodStartTs;
				let rowInsideBiggerPeriodSeconds = 0;
				if (r.periodStartTs <= biggerPeriodRow.periodStartTs) {
					// Rs,Re,Bs,Be: row & bigger start & end. --- row part outside bigger, ___ row part inside bigger
					// at or before bigger period start: Rs---Bs___Re   Be
					rowInsideBiggerPeriodSeconds = r.periodEndTs - biggerPeriodRow.periodStartTs;
				} else if (r.periodEndTs >= biggerPeriodRow.periodEndTs) {
					// at or after bigger period end: : Bs   Rs___Be---Re
					rowInsideBiggerPeriodSeconds = biggerPeriodRow.periodEndTs - r.periodStartTs;
				} else {
					// inside the period
					rowInsideBiggerPeriodSeconds = r.periodEndTs - r.periodStartTs;
				}
				rowInsideBiggerPeriodSeconds = Math.min(
					rowInsideBiggerPeriodSeconds,
					biggerPeriodSeconds,
				);
				if (rowInsideBiggerPeriodSeconds) {
					const percent = rowInsideBiggerPeriodSeconds / rowPeriodSeconds;
					rowsWithPercentage.push({ percent, row: r });
				}
			}
		});
	}
	return rowsWithPercentage;
}

function filterDailyRowsInsideBiggerPeriod(rows: GoalValueRow[], biggerPeriodRow?: GoalValueRow) {
	return biggerPeriodRow
		? rows.filter((r) => {
				// full match assumed, since we cannot have fiscal that does not include whole day in any bigger period
				return (
					r.periodStartTs >= biggerPeriodRow.periodStartTs &&
					r.periodEndTs <= biggerPeriodRow.periodEndTs
				);
			})
		: [];
}

function groupValueRowsByTimeUnit(valueRows: GoalValueRow[]) {
	const rowsByTimeUnit: ValueRowsByTimeUnit = {
		[TimeUnit.DAY]: [],
		[TimeUnit.WEEK]: [],
		[TimeUnit.MONTH]: [],
		[TimeUnit.QUARTER]: [],
		[TimeUnit.YEAR]: [],
	};
	valueRows.forEach((row) => {
		rowsByTimeUnit[row.timeUnit].push(row);
	});
	return rowsByTimeUnit;
}

function findRowWithTimestamp(rows: GoalValueRow[], ts: number) {
	return rows.find((r) => {
		return r.periodStartTs <= ts && r.periodEndTs >= ts;
	});
}

// ------------ value row & pages layout helpers -------------

function createRowsFromLayoutCache(
	formLayoutCache: APIData.Goal.FormLayout.Response,
	isMetricPercentage = false,
	isMetricInverted = false,
	currentMomentRef = moment(),
) {
	logToConsole('createRows');
	const valueRows: GoalValueRow[] = [];
	Object.keys(formLayoutCache).forEach((timeUnitString) => {
		const timeUnit: TimeUnitForPages = +timeUnitString,
			pages = formLayoutCache[timeUnit] || [];
		let previousRow: GoalValueRow | null = null;
		pages.forEach((page) => {
			page.items.forEach((item) => {
				const row = createGoalValueRowFromFormLayoutItem(
					// creates GoalValueRow instance with value null
					timeUnit,
					page.pageId,
					item,
					currentMomentRef,
					isMetricPercentage,
					isMetricInverted,
				);
				row.previousRow = previousRow;
				previousRow = row;
				valueRows.push(row);
			});
		});
	});

	return valueRows;
}

function createMissingRowsFromLayoutCache(
	valueRowsByTimeUnit: ValueRowsByTimeUnit,
	formLayoutCache: APIData.Goal.FormLayout.Response,
	isMetricPercentage = false,
	isMetricInverted = false,
	currentMomentRef = moment(),
) {
	logToConsole('createMissingRows');
	const missingRows: GoalValueRow[] = [];
	Object.keys(formLayoutCache).forEach((timeUnitString) => {
		const timeUnit: TimeUnitForPages = +timeUnitString,
			pages = formLayoutCache[timeUnit] || [],
			existingValueRows = valueRowsByTimeUnit[timeUnit],
			missingRowsForTimeUnit: GoalValueRow[] = [];
		pages.forEach((page) => {
			page.items.forEach((item) => {
				if (existingValueRows.find((existingRow) => item.id === existingRow.id)) {
					return;
				}
				const row = createGoalValueRowFromFormLayoutItem(
					timeUnit,
					page.pageId,
					item,
					currentMomentRef,
					isMetricPercentage,
					isMetricInverted,
				);
				missingRowsForTimeUnit.push(row);
			});
		});

		if (missingRowsForTimeUnit.length) {
			missingRows.push(...missingRowsForTimeUnit);

			// updating previousRow references
			const sortedRows = sortBy(
				[...existingValueRows, ...missingRowsForTimeUnit],
				'periodStartTs',
			);
			let previousRow: GoalValueRow | null = null;
			sortedRows.forEach((row) => {
				row.previousRow = previousRow;
				previousRow = row;
			});
		}
	});
	return missingRows;
}

function createGoalValueRowFromFormLayoutItem(
	timeUnit: TimeUnitForPages,
	pageId: string,
	item: APIData.Goal.FormLayout.Item,
	currentMomentRef = moment(),
	isMetricPercentage = false,
	isMetricInverted = false,
) {
	return new GoalValueRow(
		{
			id: item.id,
			title: item.title,
			periodStartTs: item.periodStartTS,
			periodEndTs: item.periodEndTS,
			timeUnit,
			pageId,
			value: null,
			isMetricPercentage,
			isMetricInverted,
		},
		currentMomentRef,
	);
}

function getPageRangeUnitForTimeUnit(timeUnit: TimeUnit) {
	// what range is the whole page (all rows)
	switch (timeUnit) {
		case TimeUnit.DAY:
			return TimeUnit.MONTH;
		case TimeUnit.WEEK:
		case TimeUnit.MONTH:
		case TimeUnit.QUARTER:
			return TimeUnit.YEAR;
		case TimeUnit.YEAR:
		default:
			return TimeUnit.TOTAL;
	}
}

function getPageIdFromFormLayoutItem(timeUnit: TimeUnit, item: APIData.Goal.FormLayout.Item) {
	let pageId = '';
	const pageRangeUnit = getPageRangeUnitForTimeUnit(timeUnit);
	if (pageRangeUnit === TimeUnit.MONTH) {
		pageId = item.id.substring(0, 7).replace('-', '-M');
	} else {
		pageId = item.id.substring(0, 4);
	}
	return pageId;
}

function getHistoryValuesFromValueRows(valueRowsByTimeUnit: ValueRowsByTimeUnit): HistoryValues {
	const newHistoryValues = Object.fromEntries(
		Object.keys(valueRowsByTimeUnit).map((timeUnit) => [timeUnit, {}]),
	);

	Object.keys(valueRowsByTimeUnit).forEach((timeUnitString: string) => {
		const timeUnit: TimeUnitForPages = +timeUnitString;
		const currentTimeUnitObject: Record<string, number | null> = newHistoryValues[timeUnit]!;

		valueRowsByTimeUnit[timeUnit].forEach((item: GoalValueRow) => {
			if (item.value !== null) {
				currentTimeUnitObject[item.id] = item.value;
			}
		});
	});

	return newHistoryValues;
}

function fillValueRowsFromHistoryValues(
	historyValues: HistoryValues,
	valueRows: GoalValueRow[],
): GoalValueRow[] {
	// go through historyValues and set values
	Object.entries(historyValues).forEach(([timeUnit, values]) => {
		const timeUnitRows = valueRows.filter((row) => row.timeUnit === +timeUnit);
		Object.entries(values).forEach(([id, value]) => {
			const row = timeUnitRows.find((row) => row.id === id);
			if (row) {
				row.value = value;
				row.markClean();
			}
		});
	});

	return valueRows;
}

// ------------ test/debug functions -------------

function generateFormLayoutItems(
	timeUnit: TimeUnitForPages,
	fromYearOffset: number,
	toYearOffset: number,
	currentMomentRef = moment(),
) {
	// only for test purposes
	const items: APIData.Goal.FormLayout.Item[] = [];

	let fromDate, toDate;
	let pageTimeUnitText: moment.unitOfTime.StartOf | null; // for moment calculations

	const timeUnitText = DateRange.getTimeUnitText(timeUnit);
	const idFormat = PeriodIdFormats[timeUnit];

	if (timeUnit === TimeUnit.YEAR) {
		const firstCurrentRowDate = currentMomentRef.clone().utc().startOf('year');
		fromDate = firstCurrentRowDate.clone().add(-5, 'year');
		toDate = firstCurrentRowDate.clone().add(5, 'year');
	} else {
		pageTimeUnitText = DateRange.getTimeUnitText(
			getPageRangeUnitForTimeUnit(timeUnit),
		) as moment.unitOfTime.StartOf;
		const firstCurrentRowDate = currentMomentRef.clone().utc().startOf(pageTimeUnitText);
		fromDate = firstCurrentRowDate
			.clone()
			.add(fromYearOffset, 'year')
			.startOf(pageTimeUnitText);
		toDate = firstCurrentRowDate.clone().add(toYearOffset, 'year').startOf(pageTimeUnitText);
	}

	if (timeUnitText) {
		const ref = fromDate.clone().startOf(timeUnitText as moment.unitOfTime.StartOf);
		while (ref.isBefore(toDate)) {
			const item = {
				id: ref.format(idFormat),
				title: ref.format(idFormat),
				periodStartTS: ref.unix(),
				periodEndTS: 0,
				additionalInfo: '',
			};
			ref.add(1, timeUnitText as moment.unitOfTime.DurationConstructor);
			item.periodEndTS = ref.unix();
			items.push(item);
		}
	}

	// divide into pages
	const pages: APIData.Goal.FormLayout.Page[] = [];
	if (timeUnit === TimeUnit.YEAR) {
		pages.push({
			pageId: 'all',
			pageName: 'All',
			pageOffset: 0,
			items,
		});
	} else {
		items.forEach((item) => {
			const pageId = getPageIdFromFormLayoutItem(timeUnit, item);
			let page = pages.find((item) => item.pageId === pageId);
			if (page) {
				page.items.push(item);
			} else {
				page = {
					pageId: pageId,
					pageOffset: 0, // updated below
					items: [item],
					pageName: pageId,
				};
				pages.push(page);
			}
		});
	}

	const currentTs = currentMomentRef.unix();
	const zeroPageOffsetIndex = pages.findIndex((page) => {
		const firstItem = page.items[0],
			lastItem = page.items.slice(-1)[0];
		return (
			firstItem &&
			lastItem &&
			firstItem.periodStartTS <= currentTs &&
			lastItem.periodEndTS >= currentTs
		);
	});
	pages.forEach((page, index) => {
		page.pageOffset = index - zeroPageOffsetIndex;
	});

	return pages;
}

function generateFormLayout(currentMomentRef: moment.Moment) {
	// only for test purposes
	const layout: APIData.Goal.FormLayout.Response = {};
	[TimeUnit.DAY, TimeUnit.WEEK, TimeUnit.MONTH, TimeUnit.QUARTER, TimeUnit.YEAR].forEach(
		(timeUnit) => {
			// load 2 years each direction
			layout[timeUnit] = generateFormLayoutItems(
				timeUnit as TimeUnitForPages,
				-2,
				2,
				currentMomentRef,
			);
		},
	);
	return layout;
}

function logToConsole(...args: unknown[]): void {
	if (window.LOGIT) {
		// eslint-disable-next-line no-console
		console.log(...args);
	} else if (window.env === 'test') {
		// eslint-disable-next-line no-console
		console.log(JSON.stringify(args));
	}
}

function getGoalRangeTimeUnitForPrimaryGranulation(primaryGranulation: GoalRangeTimeUnit) {
	// we treat all time as daily periods
	return primaryGranulation === TimeUnit.TOTAL ? TimeUnit.DAY : primaryGranulation;
}

export {
	groupValueRowsByTimeUnit,
	recalculateAllValues,
	getHistoryValuesFromValueRows,
	fillValueRowsFromHistoryValues,
	redistributeFromTimeUnit,
	createRowsFromLayoutCache,
	createMissingRowsFromLayoutCache,
	getPageRangeUnitForTimeUnit,
	findRowWithTimestamp,
	getGoalRangeTimeUnitForPrimaryGranulation,
	getShouldRecalculateAllValues,
	generateFormLayout, // for tests only
	filterDailyRowsInsideBiggerPeriod, // for tests only
	filterRowsWithPartialsInsideBiggerPeriod, // for tests only
};
